import {
  Either,
  fetchData,
  isLeft,
  left,
  right,
  TaskEither,
  taskEither,
} from '@shared-commons/utils';
import {
  TeacherListItem,
  TeacherList,
  TeacherItem,
  TeacherItemSchema,
  TeacherTopVideos,
  Course,
  CourseSchema,
} from '../types';
import { ErrorMessage } from '@/shared/utils';

// Adapter per trasformare i dati sorgenti
const teacherAdapter = (input: any): Either<Error, TeacherListItem> => {
  try {
    const adapted: TeacherListItem = {
      documentId: input.documentId,
      id: input.id,
      displayName: input.displayName || 'Sconosciuto',
      imageUrl: input.imageUrl || '',
      instruments: input.instruments || [],
    };
    return right(adapted);
  } catch (error) {
    return left(
      new Error(ErrorMessage('Failed to adapt teacher') + ': ' + (error as Error).message),
    );
  }
};

// Validatore per i dati adattati
const validateTeachers = (teachers: TeacherListItem[]): Either<Error, TeacherListItem[]> => {
  if (!Array.isArray(teachers)) {
    return left(new Error(ErrorMessage('Invalid teacher list format')));
  }

  return right(teachers);
};

// Servizio per ottenere la lista degli insegnanti
export const TeachersViewService = (): TaskEither<Error, TeacherList> => {
  return fetchData<TeacherListItem[]>(
    process.env.NEXT_PUBLIC_HOST + '/api/teacher/teachers/view', // Endpoint API
    (input: any) => {
      const adaptedTeachers = input.teachers.map((teacher: TeacherListItem) => {
        const adapted = teacherAdapter(teacher);
        if (isLeft(adapted)) {
          throw new Error(adapted.value.message);
        }
        return adapted.value;
      });
      return taskEither(async () => right(adaptedTeachers));
    },
    validateTeachers,
    'GET', // Metodo HTTP
    {}, // Configurazioni aggiuntive (se necessarie)
  );
};
const teacherDetailAdapter = (input: any): Either<Error, TeacherItem> => {
  try {
    // Estrarre i dati principali
    const data = input?.data;
    if (!data) {
      throw new Error('Missing data in API response');
    }

    // Adattare i campi della risposta API
    const adaptedTeacher: TeacherItem = {
      id: data.id,
      documentId: data.documentId,
      displayName: data.displayName || 'Unknown',
      biography: data.biography || '',
      mainSubject: data.mainSubject || '',
      profilePictureUrl: data.profilePictureUrl || '', // Corretto qui
      musicalGenres: data.musicalGenres || [],
      instruments: data.instruments || [],
      series: data.series || [],
      lessons: data.lessons || [],
      masterclass: data?.masterclass || [],
      learningPaths: data.learningPaths || [],
      artistsWorkedWith: data.artistsWorkedWith || [], // Aggiunto
      topViewedCourses: data.topViewedCourses || [], // Aggiunto
      allCourses: data.allCourses || [], // Aggiunto
    };
    return right(adaptedTeacher);
  } catch (error) {
    return left(new Error(`Failed to adapt teacher detail: ${(error as Error).message}`));
  }
};
const validateTeacherItem = (input: TeacherItem): Either<Error, TeacherItem> => {
  console.log('Validating TeacherItem:', input); // Log dei dati in ingresso

  const result = TeacherItemSchema.safeParse(input); // Usa safeParse per evitare eccezioni

  if (result.success) {
    return right(result.data);
  } else {
    console.error('Validation failed:', result.error.errors); // Log degli errori di validazione
    return left(new Error(`Validation failed: ${JSON.stringify(result.error.errors)}`));
  }
};
export const TeacherDetailView = (id: string): TaskEither<Error, TeacherItem> => {
  return fetchData<TeacherItem>(
    `${process.env.NEXT_PUBLIC_HOST}/api/teacher/${id}/view`,
    (input: any) => {
      // L'adapter deve restituire un TaskEither
      return taskEither(async () => {
        if (!input) {
          return left(new Error('Response from API is undefined'));
        }

        // Usa l'adapter esistente per adattare i dati
        const adaptedTeacher = teacherDetailAdapter(input);

        if (isLeft(adaptedTeacher)) {
          return left(adaptedTeacher.value);
        }
        return right(adaptedTeacher.value);
      });
    },
    validateTeacherItem, // Nessun validatore specificato
    'GET',
    {},
  );
};

const topVideosAdapter = (input: TeacherTopVideos): Either<Error, Course[]> => {
  try {
    // Estrarre i dati principali
    const res = input;
    if (!res) {
      throw new Error('Missing data in API response');
    }

    const skillLevelMap: Record<string, 'mai suonato' | 'base' | 'intermedio' | 'avanzato'> = {
      'Da zero': 'mai suonato',
      Principiante: 'base',
      Intermedio: 'intermedio',
      Avanzato: 'avanzato',
      'Per tutti': 'base', // Assuming "Per tutti" maps to "base",
    };

    const CourseTypeMap: Record<string, 'percorsi' | 'masterclass' | 'serie'> = {
      percorso: 'percorsi',
      masterclass: 'masterclass',
      serie: 'serie',
    };
    // Adattare i campi della risposta API
    const adaptedTeacherTopVideos: Course[] = res.map<Course>((el) => ({
      type: (el.type?.toLowerCase() as 'percorso' | 'serie' | 'masterclass') || 'serie',
      courseType: CourseTypeMap?.[el.courseType?.toLowerCase() ?? 'serie'],
      description: el.description,
      documentId: el.documentId,
      duration: el.duration.toString(),
      id: el.id.toString(),
      highlighted: el?.isHighlighted || false,
      isPurchasable: el.isPurchasable,
      playbackId: el?.playbackId || null,
      title: el.title,
      teachers:
        el.teacherProfiles?.map((t) => ({
          displayName: t.displayName,
          avatarUrl: t.profilePicture?.url || '',
        })) || [],
      genres: [],
      videoCount: el.numberOfVideos || 0,
      courseImageUrl: el.thumbnail?.url || '',
      level:
        (el.skillLevel?.name as
          | 'Da zero'
          | 'Principiante'
          | 'Intermedio'
          | 'Avanzato'
          | 'Per tutti') || 'Da zero',
    }));

    return right(adaptedTeacherTopVideos);
  } catch (error) {
    return left(new Error(`Failed to adapt teacher detail: ${(error as Error).message}`));
  }
};

const validateTeacherTopVideos = (input: Course[]): Either<Error, Course[]> => {
  console.log('Validating TeacherTopVideos:', input); // Log dei dati in ingresso

  const result = CourseSchema.array().safeParse(input); // Usa safeParse per evitare eccezioni

  if (result.success) {
    return right(result.data);
  } else {
    console.error('Validation failed:', result.error.errors); // Log degli errori di validazione
    return left(new Error(`Validation failed: ${JSON.stringify(result.error.errors)}`));
  }
};

export const TeacherTopVideosView = (id: string): TaskEither<Error, Course[]> => {
  return fetchData<Course[]>(
    `${process.env.NEXT_PUBLIC_HOST}/api/teacher/${id}/top-videos`,
    (input: TeacherTopVideos) => {
      // L'adapter deve restituire un TaskEither

      return taskEither(async () => {
        if (!input) {
          return left(new Error('Response from API is undefined'));
        }
        // Usa l'adapter esistente per adattare i dati
        const adaptedTeacherTopVideos = topVideosAdapter(input);

        if (isLeft(adaptedTeacherTopVideos)) {
          return left(adaptedTeacherTopVideos.value);
        }
        return right(adaptedTeacherTopVideos.value);
      });
    },
    validateTeacherTopVideos, // Nessun validatore specificato
    'GET',
    {},
  );
};
